import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import {
  useBlockSelector,
  bundles as bundlesHelper,
  useBuyStoreRawItemMutation,
} from '@site-builder/block-helpers';
import { useQueryClient } from '@tanstack/react-query';

import * as styles from './block.css';
import { PackCardWithModal } from './components/PackCardWithModal';
import { FormattedMessage, isItemPurchased } from './utils';

import type { Bundle, Item } from '@site-builder/store-sdk';

const App: FC = () => {
  const bundlesGroup = useBlockSelector((b) => b?.bundlesGroup);

  useEffect(() => {
    if (!bundlesGroup) {
      document.dispatchEvent(
        new CustomEvent('packs-block:visibility', {
          detail: { visible: false },
        }),
      );
    }
  }, [bundlesGroup]);

  if (!bundlesGroup) {
    return null;
  }

  return <PacksContent bundlesGroup={bundlesGroup} />;
};

const PacksContent: FC<{
  bundlesGroup: string;
}> = ({ bundlesGroup }) => {
  const bundlesQuery =
    bundlesHelper.useFiniteGroupQuery<Bundle[]>(bundlesGroup);
  const buyMutation = useBuyStoreRawItemMutation();
  const queryClient = useQueryClient();

  const { data: bundleData, isLoading, isError } = bundlesQuery;

  const availableBundles = useMemo(
    () => (bundleData ?? []).filter((item) => !isItemPurchased(item as Item)),
    [bundleData],
  );

  const isVisible = !isLoading && !isError && availableBundles.length > 0;

  useEffect(() => {
    document.dispatchEvent(
      new CustomEvent('packs-block:visibility', {
        detail: { visible: isVisible },
      }),
    );
  }, [isVisible]);

  const handlePurchase = useCallback(
    (bundle: Bundle) => {
      buyMutation.mutate(bundle, {
        onSuccess: async () => {
          await queryClient.refetchQueries({
            queryKey: ['store-item'],
            type: 'active',
          });
          bundlesQuery.refetch();
        },
      });
    },
    [buyMutation, queryClient, bundlesQuery],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`${styles.wrapper} new-store__section--packs`}>
      <div className={styles.container}>
        <div className={`${styles.mainTitle} xds-text xds-text-heading`}>
          <h2 className={styles.title}>
            <FormattedMessage id='packs.blockTitle' />
          </h2>
        </div>
        <div className={styles.grid}>
          {availableBundles.map((item) => (
            <PackCardWithModal
              key={item.sku}
              data={item}
              onPurchase={() => handlePurchase(item)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default App;
